import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { ForumThread, DirectMessage, Notification, User, SocialPost } from '../types'
import NotificationSystem from '../components/NotificationSystem'
import ReactionSystem, { Reaction } from '../components/ReactionSystem'
import VoteSystem from '../components/VoteSystem'
import FriendsList from '../components/FriendsList'
import LoginForm from '../components/LoginForm'
import RegisterForm from '../components/RegisterForm'

// Mock data for the social hub
const mockRecentThreads: ForumThread[] = [
  {
    id: 'thread1',
    categoryId: 'general',
    title: 'Best morning strains for energy?',
    content: 'Looking for recommendations for strains that give good energy without jitters. What do you all use?',
    authorId: 'user1',
    authorUsername: 'EnergySeeker',
    authorRole: 'member',
    dateCreated: new Date('2024-12-08T11:30:00Z'),
    lastActivity: new Date('2024-12-08T14:30:00Z'),
    isPinned: false,
    isLocked: false,
    isDeleted: false,
    views: 156,
    postCount: 23,
    upvotes: 45,
    downvotes: 2,
    tags: ['energy', 'morning', 'strains']
  },
  {
    id: 'thread2',
    categoryId: 'strain-reviews',
    title: 'Red Dragon - Detailed Review',
    content: 'Just tried Red Dragon from a new vendor. Here\'s my detailed experience...',
    authorId: 'user2',
    authorUsername: 'StrainReviewer',
    authorRole: 'trusted',
    dateCreated: new Date('2024-12-08T10:45:00Z'),
    lastActivity: new Date('2024-12-08T13:15:00Z'),
    isPinned: false,
    isLocked: false,
    isDeleted: false,
    views: 234,
    postCount: 18,
    upvotes: 67,
    downvotes: 3,
    tags: ['review', 'red-dragon', 'effects']
  }
]

const mockTrendingTopics = [
  { tag: 'green-malay', count: 45, trend: 'up' },
  { tag: 'lab-results', count: 38, trend: 'up' },
  { tag: 'dosing-help', count: 29, trend: 'up' },
  { tag: 'vendor-review', count: 25, trend: 'down' },
  { tag: 'extraction', count: 22, trend: 'stable' }
]

const mockActiveUsers: User[] = [
  {
    id: 'user1',
    username: 'KratomExpert',
    email: 'expert@example.com',
    dateOfBirth: new Date('1990-01-01'),
    isAgeVerified: true,
    dateCreated: new Date('2024-01-01'),
    isVerified: true,
    role: 'trusted'
  },
  {
    id: 'vendor1',
    username: 'QualityVendor',
    email: 'vendor@example.com',
    dateOfBirth: new Date('1985-01-01'),
    isAgeVerified: true,
    dateCreated: new Date('2023-06-01'),
    isVerified: true,
    role: 'verified_vendor'
  },
  {
    id: 'user2',
    username: 'StrainExpert',
    email: 'strain@example.com',
    dateOfBirth: new Date('1992-01-01'),
    isAgeVerified: true,
    dateCreated: new Date('2024-03-01'),
    isVerified: false,
    role: 'member'
  }
]

const mockSocialPosts: SocialPost[] = [
  {
    id: 'social1',
    authorId: 'user1',
    authorUsername: 'KratomExpert',
    authorRole: 'trusted',
    type: 'text',
    content: 'Just tried the new Green Malay batch from @QualityVendor - absolutely amazing quality! The effects are exactly what I was looking for. 🌿',
    dateCreated: new Date('2024-12-08T15:30:00Z'),
    isDeleted: false,
    likes: 24,
    comments: 8,
    shares: 3,
    visibility: 'public',
    tags: ['green-malay', 'review']
  },
  {
    id: 'social2',
    authorId: 'vendor1',
    authorUsername: 'QualityVendor',
    authorRole: 'verified_vendor',
    type: 'text',
    content: 'New lab results are in for our latest Red Bali batch! COA shows 1.7% mitragynine and <0.02% 7-OH. Quality you can trust! 🧪',
    dateCreated: new Date('2024-12-08T14:15:00Z'),
    isDeleted: false,
    likes: 56,
    comments: 12,
    shares: 18,
    visibility: 'public',
    tags: ['lab-results', 'red-bali', 'quality']
  }
]

function formatTimestamp(date: Date): string {
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString()
}

function getRoleColor(role: string): string {
  switch (role) {
    case 'admin': return '#e53e3e'
    case 'moderator': return '#9f7aea'
    case 'verified_vendor': return '#00d166'
    case 'trusted': return '#ffd700'
    default: return '#a0aec0'
  }
}

function getRoleBadge(role: string): string {
  switch (role) {
    case 'admin': return '👑'
    case 'moderator': return '🛡️'
    case 'verified_vendor': return '✅'
    case 'trusted': return '🏆'
    default: return ''
  }
}

function getTrendIcon(trend: string): string {
  switch (trend) {
    case 'up': return '📈'
    case 'down': return '📉'
    default: return '➡️'
  }
}

export default function Social() {
  const { user, isLoggedIn } = useAuth()
  const [activeTab, setActiveTab] = useState<'feed' | 'threads' | 'activity'>('feed')
  const [newPostContent, setNewPostContent] = useState('')
  
  // Modal state management
  const [showLoginForm, setShowLoginForm] = useState(false)
  const [showRegisterForm, setShowRegisterForm] = useState(false)
  
  // Modal handler functions
  const handleLoginClick = () => setShowLoginForm(true)
  const handleRegisterClick = () => setShowRegisterForm(true)
  
  const closeAuthForms = () => {
    setShowLoginForm(false)
    setShowRegisterForm(false)
  }
  
  const switchToRegister = () => {
    setShowLoginForm(false)
    setShowRegisterForm(true)
  }
  
  const switchToLogin = () => {
    setShowRegisterForm(false)
    setShowLoginForm(true)
  }

  const handleCreatePost = () => {
    if (!newPostContent.trim() || !user) return

    const newPost: SocialPost = {
      id: `social_${Date.now()}`,
      authorId: user.id,
      authorUsername: user.username,
      authorRole: user.role,
      type: 'text',
      content: newPostContent.trim(),
      dateCreated: new Date(),
      isDeleted: false,
      likes: 0,
      comments: 0,
      shares: 0,
      visibility: 'public',
      tags: []
    }

    // TODO: API call to create post
    console.log('Creating social post:', newPost)
    setNewPostContent('')
  }

  if (!isLoggedIn) {
    return (
      <div className="social-page">
        <div className="container">
          <div className="auth-required">
            <h1>Join the Community</h1>
            <p>Login to access the social hub and connect with fellow kratom enthusiasts.</p>
            <div className="auth-actions">
              <button className="btn-primary" onClick={handleLoginClick}>Login</button>
              <button className="btn-secondary" onClick={handleRegisterClick}>Sign Up</button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="social-page">
      <div className="container">
        {/* Social Hub Header */}
        <div className="social-header">
          <div className="header-content">
            <h1>Social Hub</h1>
            <p>Connect, share, and engage with the kratom community</p>
          </div>
          
          <div className="header-stats">
            <div className="stat">
              <span className="stat-number">2,847</span>
              <span className="stat-label">Active Members</span>
            </div>
            <div className="stat">
              <span className="stat-number">156</span>
              <span className="stat-label">Online Now</span>
            </div>
            <div className="stat">
              <span className="stat-number">1,234</span>
              <span className="stat-label">Posts Today</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions">
          <Link to="/community/create-thread" className="action-card">
            <span className="action-icon">📝</span>
            <div className="action-content">
              <h3>Start Discussion</h3>
              <p>Create a new thread</p>
            </div>
          </Link>
          
          <Link to="/messages" className="action-card">
            <span className="action-icon">✉️</span>
            <div className="action-content">
              <h3>Messages</h3>
              <p>Chat privately</p>
            </div>
          </Link>
          
          <Link to="/community" className="action-card">
            <span className="action-icon">💬</span>
            <div className="action-content">
              <h3>Forums</h3>
              <p>Browse categories</p>
            </div>
          </Link>
          
          <Link to="/profile" className="action-card">
            <span className="action-icon">👤</span>
            <div className="action-content">
              <h3>Profile</h3>
              <p>View your activity</p>
            </div>
          </Link>
        </div>

        <div className="social-layout">
          {/* Main Content */}
          <div className="social-main">
            {/* Create Post */}
            <div className="create-post-card">
              <div className="create-post-header">
                <div className="user-avatar">
                  <div className="avatar-placeholder">
                    {user.username[0].toUpperCase()}
                  </div>
                </div>
                <div className="create-post-input">
                  <textarea
                    value={newPostContent}
                    onChange={(e) => setNewPostContent(e.target.value)}
                    placeholder="Share your thoughts with the community..."
                    rows={3}
                  />
                </div>
              </div>
              <div className="create-post-actions">
                <div className="post-options">
                  <button className="option-btn" title="Add image">📷</button>
                  <button className="option-btn" title="Add poll">📊</button>
                  <button className="option-btn" title="Tag strain">🌿</button>
                </div>
                <button 
                  onClick={handleCreatePost}
                  disabled={!newPostContent.trim()}
                  className="post-btn"
                >
                  Post
                </button>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="social-tabs">
              <button 
                onClick={() => setActiveTab('feed')}
                className={`tab ${activeTab === 'feed' ? 'active' : ''}`}
              >
                Social Feed
              </button>
              <button 
                onClick={() => setActiveTab('threads')}
                className={`tab ${activeTab === 'threads' ? 'active' : ''}`}
              >
                Recent Threads
              </button>
              <button 
                onClick={() => setActiveTab('activity')}
                className={`tab ${activeTab === 'activity' ? 'active' : ''}`}
              >
                Community Activity
              </button>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
              {activeTab === 'feed' && (
                <div className="social-feed">
                  {mockSocialPosts.map(post => (
                    <div key={post.id} className="social-post">
                      <div className="post-header">
                        <Link to={`/profile/${post.authorId}`} className="post-author">
                          <div className="author-avatar">
                            <div className="avatar-placeholder">
                              {post.authorUsername[0].toUpperCase()}
                            </div>
                          </div>
                          <div className="author-info">
                            <span 
                              className="author-name"
                              style={{ color: getRoleColor(post.authorRole) }}
                            >
                              {getRoleBadge(post.authorRole)} {post.authorUsername}
                            </span>
                            <span className="post-time">
                              {formatTimestamp(post.dateCreated)}
                            </span>
                          </div>
                        </Link>
                      </div>
                      
                      <div className="post-content">
                        <p>{post.content}</p>
                        {post.tags.length > 0 && (
                          <div className="post-tags">
                            {post.tags.map(tag => (
                              <span key={tag} className="tag">#{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <div className="post-interactions">
                        <ReactionSystem
                          targetId={post.id}
                          targetType="post"
                          reactions={[]} // Mock empty for now
                          onReact={(type) => console.log(`Reacted ${type} to ${post.id}`)}
                          onRemoveReaction={() => console.log(`Removed reaction from ${post.id}`)}
                          showLabels={false}
                          size="small"
                        />
                        
                        <div className="interaction-stats">
                          <span className="stat">💬 {post.comments}</span>
                          <span className="stat">🔄 {post.shares}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'threads' && (
                <div className="recent-threads">
                  {mockRecentThreads.map(thread => (
                    <div key={thread.id} className="thread-card">
                      <div className="thread-main">
                        <div className="thread-votes">
                          <VoteSystem
                            targetId={thread.id}
                            targetType="thread"
                            initialUpvotes={thread.upvotes}
                            initialDownvotes={thread.downvotes}
                            onVote={(type) => console.log(`Voted ${type} on ${thread.id}`)}
                            onRemoveVote={() => console.log(`Removed vote from ${thread.id}`)}
                            orientation="vertical"
                            size="small"
                            animated={false}
                          />
                        </div>
                        
                        <div className="thread-content">
                          <div className="thread-header">
                            <Link to={`/community/thread/${thread.id}`} className="thread-title">
                              {thread.title}
                            </Link>
                            <div className="thread-meta">
                              <Link 
                                to={`/profile/${thread.authorId}`}
                                className="thread-author"
                                style={{ color: getRoleColor(thread.authorRole) }}
                              >
                                {getRoleBadge(thread.authorRole)} {thread.authorUsername}
                              </Link>
                              <span className="thread-time">
                                {formatTimestamp(thread.dateCreated)}
                              </span>
                            </div>
                          </div>
                          
                          <p className="thread-preview">{thread.content.slice(0, 150)}...</p>
                          
                          <div className="thread-stats">
                            <span className="stat">{thread.views} views</span>
                            <span className="stat">{thread.postCount} replies</span>
                            {thread.tags.length > 0 && (
                              <div className="thread-tags">
                                {thread.tags.slice(0, 3).map(tag => (
                                  <span key={tag} className="tag">#{tag}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'activity' && (
                <div className="community-activity">
                  <div className="activity-feed">
                    <div className="activity-item">
                      <span className="activity-icon">📝</span>
                      <div className="activity-content">
                        <strong>KratomExpert</strong> created a new thread
                        <Link to="/community/thread/thread1">"Best morning strains for energy?"</Link>
                        <span className="activity-time">2 hours ago</span>
                      </div>
                    </div>
                    
                    <div className="activity-item">
                      <span className="activity-icon">💬</span>
                      <div className="activity-content">
                        <strong>QualityVendor</strong> replied to
                        <Link to="/community/thread/thread2">"Red Dragon - Detailed Review"</Link>
                        <span className="activity-time">3 hours ago</span>
                      </div>
                    </div>
                    
                    <div className="activity-item">
                      <span className="activity-icon">⭐</span>
                      <div className="activity-content">
                        <strong>StrainExpert</strong> earned the "Helpful Reviewer" badge
                        <span className="activity-time">5 hours ago</span>
                      </div>
                    </div>
                    
                    <div className="activity-item">
                      <span className="activity-icon">👤</span>
                      <div className="activity-content">
                        <strong>NewMember</strong> joined the community
                        <span className="activity-time">6 hours ago</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="social-sidebar">
            {/* Friends List */}
            <div className="sidebar-section">
              <FriendsList maxVisible={4} showHeader={true} compact={false} />
            </div>

            {/* Trending Topics */}
            <div className="sidebar-section">
              <h3>Trending Topics</h3>
              <div className="trending-list">
                {mockTrendingTopics.map(topic => (
                  <div key={topic.tag} className="trending-item">
                    <div className="trending-content">
                      <span className="trending-tag">#{topic.tag}</span>
                      <span className="trending-count">{topic.count} posts</span>
                    </div>
                    <span className="trending-icon">{getTrendIcon(topic.trend)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Active Users */}
            <div className="sidebar-section">
              <h3>Active Community Members</h3>
              <div className="active-users">
                {mockActiveUsers.map(activeUser => (
                  <Link 
                    key={activeUser.id} 
                    to={`/profile/${activeUser.id}`}
                    className="active-user"
                  >
                    <div className="user-avatar">
                      <div className="avatar-placeholder">
                        {activeUser.username[0].toUpperCase()}
                      </div>
                      <div className="online-indicator">🟢</div>
                    </div>
                    <div className="user-info">
                      <span 
                        className="username"
                        style={{ color: getRoleColor(activeUser.role) }}
                      >
                        {getRoleBadge(activeUser.role)} {activeUser.username}
                      </span>
                      <span className="user-status">Online</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Notifications */}
            <div className="sidebar-section">
              <NotificationSystem 
                showInline={true}
                maxVisible={3}
              />
            </div>

            {/* Quick Stats */}
            <div className="sidebar-section">
              <h3>Your Activity</h3>
              <div className="user-quick-stats">
                <div className="quick-stat">
                  <span className="stat-number">23</span>
                  <span className="stat-label">Posts</span>
                </div>
                <div className="quick-stat">
                  <span className="stat-number">156</span>
                  <span className="stat-label">Comments</span>
                </div>
                <div className="quick-stat">
                  <span className="stat-number">45</span>
                  <span className="stat-label">Followers</span>
                </div>
                <div className="quick-stat">
                  <span className="stat-number">89</span>
                  <span className="stat-label">Following</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Authentication Forms */}
      {showLoginForm && (
        <LoginForm onClose={closeAuthForms} onSwitchToRegister={switchToRegister} />
      )}
      {showRegisterForm && (
        <RegisterForm onClose={closeAuthForms} onSwitchToLogin={switchToLogin} />
      )}
    </div>
  )
}